/* What the two detail pages (a site, a source) share: the status row under the header, a tab area
   that stacks full-width panels, list rows, and the state tally. Colours come from tokens only. */

.tabArea {
  grid-column: 1 / -1;
  min-width: 0;
}

.stack {
  display: flex;
  flex-direction: column;
  gap: var(--gap);
  min-width: 0;
}

.stack > * {
  flex: none;
}

.statusRow {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.band {
  grid-column: 1 / -1;
  min-width: 0;
}

.kv {
  display: grid;
  grid-template-columns: minmax(120px, 190px) minmax(0, 1fr);
  gap: 6px 14px;
  margin: 0;
  font-size: var(--t-min);
}

.kv dt {
  color: var(--muted);
}

.kv dd {
  margin: 0;
  color: var(--ink);
  overflow-wrap: anywhere;
}

.note {
  margin: 0;
  max-width: 92ch;
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

.list {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}

.row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 6px 14px;
  padding: 10px 0;
  border-bottom: 1px solid var(--line);
  min-width: 0;
}

.rowMain {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.rowName {
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
  overflow-wrap: anywhere;
}

.rowMeta {
  font-size: var(--t-min);
  color: var(--muted);
  overflow-wrap: anywhere;
}

.rowSide {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px 12px;
  font-size: var(--t-min);
}

.value {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: lining-nums tabular-nums;
}

/* WCAG 2.5.8: a link or text button that stands alone is a 24px target even when its text is 17px high
   (gate 37 found "Back to Sources" at 95x17 beside another control). `inline-flex` so the box is the
   target and a line of prose around it does not move. */
.link {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  color: var(--accent);
  font-size: var(--t-min);
}

.action {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  font: inherit;
  font-size: var(--t-min);
  color: var(--accent);
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
}

.action:focus-visible,
.link:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  align-items: center;
  margin-bottom: 8px;
  font-size: var(--t-min);
  color: var(--muted);
}

/* ---------------------------------------------------------------- the state tally */

.tally {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.tallyItem {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 6px;
  border: 1px solid var(--line);
  border-radius: 4px;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--ink-2);
  font-variant-numeric: lining-nums tabular-nums;
}

.tallyItem[data-state="missing"] {
  color: var(--warn);
  background: var(--warn-w);
  border-color: var(--warn);
}

.tallyItem[data-state="pending"] {
  border-style: dashed;
}

.tallyItem[data-state="declared_nil"] {
  background: var(--panel-2);
}

.tallyItem[data-state="not_expected"] {
  color: var(--muted);
  border-color: var(--line-2);
}

/* ---------------------------------------------------------------- the twelve-window strip */

.windows {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 210px), 1fr));
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.window {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 8px 10px;
  border: 1px solid var(--line);
  border-radius: 6px;
  font-size: var(--t-min);
  color: var(--ink-2);
}

.window[data-state="missing"] {
  border-color: var(--warn);
  background: var(--warn-w);
}

.window[data-state="pending"] {
  border-style: dashed;
}

.window[data-state="declared_nil"],
.window[data-state="unknown"] {
  background: var(--panel-2);
}

.window[data-state="not_expected"] {
  border-color: var(--line-2);
  color: var(--muted);
}

.windowHead {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 650;
  color: var(--ink);
}

.windowDates {
  color: var(--muted);
}

.windowList {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.superseded {
  color: var(--warn);
}

/* The short line under a tile's figure on a site page: what the figure rests on, in a few words. */
.tileNote {
  color: var(--muted);
  font-size: var(--t-min);
}
